import * as React from 'react';
import {useRef, useState, useCallback, useEffect} from 'react';
import {KatButton, KatModal} from '@amzn/katal-react';
import {ReviewResult, ReviewStatus} from 'src/app/constant/Enums';
import {MAX_COMMENT_LENGTH} from 'src/app/api/model/request/AddCommentRequest';
import LighthouseOffboardModal from 'src/app/component/lighthouse/review/LighthouseOffboardModal';

export interface DecisionFooterProps {
    /** Available decision options (e.g. ['OFFBOARDED', 'NO_ACTION']). */
    decisions: string[];
    /** Reason code dropdown options. */
    reasonCodes: {name: string; value: string}[];
    /** Backend-sourced reason code options for the offboard modal. Falls back to reasonCodes when empty. */
    offboardReasonCodes?: {value: string; title: string}[];
    /** Root cause options for the offboard modal. Empty until LMAQ delivers them (dropdown stays greyed out). */
    rootCauses?: {value: string; title: string}[];
    /** Callback when a decision is confirmed. */
    onSubmit: (decision: string, notes: string, reasonCode: string, rootCause: string) => void;
    /** Disables all controls (e.g. during submission or in readOnly mode). */
    disabled: boolean;
    /** Called when the "+ Upload File" button is clicked. Opens the upload modal (owned by the parent). */
    onUploadClick?: () => void;
    /** Called when "Add Remark" is clicked, with the current Auditor Remarks text. Parent posts it as a comment; resolves true on success (remarks are then cleared). */
    onAddRemark?: (remark: string) => Promise<boolean>;
    /** Current review status. Used to gate the Pause button (shown only when PENDING). */
    reviewStatus?: string;
    /** Called when Pause is clicked, with the current Auditor Remarks text as an optional note. */
    onPause?: (notes: string) => void;
    /** Called when "Create ticket for SLP" is clicked. The button renders only when supplied,
     *  so the parent decides eligibility (ODQ in a supported market). */
    onSLPClick?: () => void;
}

// Decision-code → button label. Keys are ReviewResult tokens (the values the backend serves in
// `decisions` and that SubmitDecision accepts), so they must match the model enum exactly.
const DECISION_LABELS: Record<string, string> = {
    [ReviewResult.OFFBOARDED]: 'Offboard DP',
    [ReviewResult.NO_ACTION]: 'No Action',
    [ReviewResult.RE_INSTATED]: 'Reinstate DP',
    [ReviewResult.NO_RE_INSTATED]: 'Do Not Reinstate',
    [ReviewResult.INACTIVATED]: 'Inactivate'
};

const DESTRUCTIVE_DECISIONS = new Set<string>([ReviewResult.OFFBOARDED, ReviewResult.INACTIVATED]);

/** Min/max height for the drag-resizable textarea (px). */
const MIN_TEXTAREA_HEIGHT = 40;
const MAX_TEXTAREA_HEIGHT = 500;

/**
 * Returns a human-readable label for a decision code.
 */
export const getButtonLabel = (decision: string): string => {
    return DECISION_LABELS[decision] || decision.replace(/_/g, ' ');
};

/**
 * Decision action bar at the bottom of the review page.
 * Layout:
 *   Row 1: Auditor Remarks (resizable) | Reason dropdown | Decision buttons
 *   Row 2: Upload File placeholder button
 * Shows a confirmation modal before submitting.
 */
const DecisionFooter: React.FC<DecisionFooterProps> = ({decisions, reasonCodes, offboardReasonCodes, rootCauses, onSubmit, disabled, onUploadClick, onAddRemark, reviewStatus, onPause, onSLPClick}) => {
    const notesRef = useRef<HTMLTextAreaElement>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showOffboardModal, setShowOffboardModal] = useState(false);
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [pauseReason, setPauseReason] = useState('');
    const [pendingDecision, setPendingDecision] = useState<string | null>(null);
    const [textareaHeight, setTextareaHeight] = useState(MIN_TEXTAREA_HEIGHT);
    // Live character count for the Auditor Remarks, shown next to the label so auditors
    // know how close they are to MAX_COMMENT_LENGTH (the same limit the submit validation enforces).
    const [notesLength, setNotesLength] = useState(0);

    // Drag-to-resize state
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startHeight = useRef(MIN_TEXTAREA_HEIGHT);

    const handleDragMove = useCallback((e: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = startY.current - e.clientY;
        const newHeight = Math.max(MIN_TEXTAREA_HEIGHT, Math.min(startHeight.current + delta, MAX_TEXTAREA_HEIGHT));
        setTextareaHeight(newHeight);
    }, []);

    const handleDragEnd = useCallback(() => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
    }, [handleDragMove]);

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        isDragging.current = true;
        startY.current = e.clientY;
        startHeight.current = textareaHeight;
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        e.preventDefault();
    }, [textareaHeight, handleDragMove, handleDragEnd]);

    // Cleanup document-level listeners on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [handleDragMove, handleDragEnd]);

    const handleDecisionClick = (decision: string) => {
        setPendingDecision(decision);
        if (decision === ReviewResult.OFFBOARDED) {
            setShowOffboardModal(true);
        } else {
            setShowConfirmation(true);
        }
    };

    const handleConfirm = () => {
        if (pendingDecision) {
            const notes = notesRef.current?.value || '';
            onSubmit(pendingDecision, notes, '', '');
        }
        setShowConfirmation(false);
        setPendingDecision(null);
    };

    const handleOffboardSubmit = (reasonCode: string, rootCause: string) => {
        const notes = notesRef.current?.value || '';
        onSubmit(ReviewResult.OFFBOARDED, notes, reasonCode, rootCause);
        setShowOffboardModal(false);
        setPendingDecision(null);
    };

    const handleOffboardClose = () => {
        setShowOffboardModal(false);
        setPendingDecision(null);
    };

    const handleCancel = () => {
        setShowConfirmation(false);
        setPendingDecision(null);
    };

    const handleUploadClick = () => {
        onUploadClick?.();
    };

    const handlePauseClick = () => {
        setPauseReason('');
        setShowPauseModal(true);
    };

    const handlePauseConfirm = () => {
        onPause?.(pauseReason.trim());
        setShowPauseModal(false);
        setPauseReason('');
    };

    const handlePauseCancel = () => {
        setShowPauseModal(false);
        setPauseReason('');
    };

    // Pause is offered only for a still-open (PENDING) case; resuming happens from the Management page.
    const showPause = !!onPause && reviewStatus === ReviewStatus.PENDING;

    const showSLP = !!onSLPClick;

    const handleAddRemarkClick = async () => {
        if (!onAddRemark) return;
        const added = await onAddRemark(notesRef.current?.value || '');
        // Clear the Auditor Remarks only after a successful add.
        if (added && notesRef.current) {
            notesRef.current.value = '';
            setNotesLength(0);
        }
    };

    return (
        <div data-testid="decision-footer">
            {/* Row 1: Remarks + Reason + Buttons */}
            <div className="lighthouse-decision-row">
                <div className="lighthouse-remarks-wrapper">
                    <div className="lighthouse-remarks-header">
                        <label className="lighthouse-remarks-label" htmlFor="auditor-remarks">
                            Auditor Remarks
                        </label>
                        <span
                            className="lighthouse-remarks-drag-handle"
                            onMouseDown={handleDragStart}
                            title="Drag to resize"
                            data-testid="drag-handle"
                        >
                            ⋮⋮
                        </span>
                    </div>
                    {/* The textarea + Add button share one relative box, so the button is
                        anchored to the TEXTAREA (not the outer wrapper). The counter lives
                        outside this box, below it, so it never shifts the button. */}
                    <div className="lighthouse-remarks-input">
                        <textarea
                            id="auditor-remarks"
                            className="lighthouse-remarks-textarea"
                            style={{height: `${textareaHeight}px`}}
                            disabled={disabled}
                            ref={notesRef}
                            maxLength={MAX_COMMENT_LENGTH}
                            onChange={(e) => setNotesLength(e.target.value.length)}
                            placeholder="Enter remarks..."
                            data-testid="auditor-remarks"
                        />
                        {/* Overlay "Add" button pinned to the textarea's bottom-right (chat-style). */}
                        <KatButton
                            label="Add"
                            variant="primary"
                            onClick={handleAddRemarkClick}
                            disabled={disabled}
                            className="lighthouse-remark-btn"
                            data-testid="add-remark-btn"
                        />
                    </div>
                    {/* Character count on its own line below the input box. */}
                    <span
                        className={`lighthouse-remarks-charcount${
                            notesLength >= MAX_COMMENT_LENGTH ? ' lighthouse-remarks-charcount--limit' : ''
                        }`}
                        data-testid="remarks-charcount"
                    >
                        {notesLength} / {MAX_COMMENT_LENGTH}
                    </span>
                </div>
                {showPause && (
                    <KatButton
                        label="Pause"
                        variant="secondary"
                        onClick={handlePauseClick}
                        disabled={disabled}
                        data-testid="pause-btn"
                    />
                )}
                {decisions.map((decision) => (
                    <KatButton
                        key={decision}
                        label={getButtonLabel(decision)}
                        onClick={() => handleDecisionClick(decision)}
                        disabled={disabled}
                        className={DESTRUCTIVE_DECISIONS.has(decision) ? 'amazon-orange' : ''}
                        data-testid={`decision-btn-${decision}`}
                    />
                ))}
                {/* Not a ReviewResult, so it cannot come from `decisions`; it opens the SLP
                    ticket modal rather than submitting a decision. The footer stays generic —
                    the caller decides which case types and markets are eligible by choosing
                    whether to pass onSLPClick at all. */}
                {showSLP && (
                    <KatButton
                        label="Create ticket for SLP"
                        onClick={onSLPClick}
                        disabled={disabled}
                        data-testid="lighthouse-slp-btn"
                    />
                )}
            </div>

            {/* Row 2: Upload */}
            <div className="lighthouse-upload-row">
                <KatButton
                    label="+ Upload File"
                    variant="secondary"
                    onClick={handleUploadClick}
                    disabled={disabled}
                    data-testid="upload-file-btn"
                />
            </div>

            {/* Confirmation Modal */}
            <KatModal
                visible={showConfirmation}
                onClose={handleCancel}
                data-testid="decision-confirmation-modal"
            >
                <span slot="title">Confirm Decision</span>
                <div>
                    <p>
                        Are you sure you want to submit the decision:{' '}
                        <strong>{pendingDecision ? getButtonLabel(pendingDecision) : ''}</strong>?
                    </p>
                    {notesRef.current?.value && (
                        <p style={{whiteSpace: 'pre-wrap'}}><em>Remarks: {notesRef.current.value}</em></p>
                    )}
                </div>
                <div slot="footer" className="lighthouse-modal-footer">
                    <KatButton label="Cancel" variant="link" onClick={handleCancel} data-testid="confirm-cancel" />
                    <KatButton label="Confirm" onClick={handleConfirm} className="amazon-orange" data-testid="confirm-submit" />
                </div>
            </KatModal>

            {/* Pause Modal — a reason is required so the case history records why the case was paused */}
            <KatModal
                visible={showPauseModal}
                onClose={handlePauseCancel}
                data-testid="pause-confirmation-modal"
            >
                <span slot="title">Pause Case</span>
                <div>
                    <p>Add a reason for pausing this case.</p>
                    <textarea
                        className="lighthouse-remarks-textarea"
                        value={pauseReason}
                        maxLength={MAX_COMMENT_LENGTH}
                        onChange={(e) => setPauseReason(e.target.value)}
                        placeholder="Reason for pausing..."
                        data-testid="pause-reason"
                    />
                </div>
                <div slot="footer" className="lighthouse-modal-footer">
                    <KatButton label="Cancel" variant="link" onClick={handlePauseCancel} data-testid="pause-cancel" />
                    <KatButton
                        label="Confirm Pause"
                        onClick={handlePauseConfirm}
                        disabled={pauseReason.trim().length === 0}
                        data-testid="pause-confirm"
                    />
                </div>
            </KatModal>

            {/* Offboard Decision Modal — reason code mandatory, root cause shown but greyed out until LMAQ delivers options */}
            <LighthouseOffboardModal
                visible={showOffboardModal}
                reasonCodes={offboardReasonCodes && offboardReasonCodes.length > 0
                    ? offboardReasonCodes
                    : reasonCodes.map((rc) => ({value: rc.value, title: rc.name}))}
                rootCauses={rootCauses}
                onSubmit={handleOffboardSubmit}
                onClose={handleOffboardClose}
            />
        </div>
    );
};

export default DecisionFooter;
